import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { FiTrash2 } from "react-icons/fi";

import NavBar from "../../Componentes/NavBar";
import SortableTable from "../../Componentes/Utils/SortableTable";
import FilterContainer from "../../Componentes/Utils/FilterContainer";
import useApi from "../../hooks/useApi";
import { useModal } from "../../contexts/ModalContext";
import SidebarFolhaPag from "./SidebarFolhaPag";
import ModalNovoLancamento from "./ModalNovoLancamento";
import ConfirmDeleteLancamento from "./ConfirmDeleteLancamento";

const FOLHA_API = {
    valores: "/Painel/API/FolhaPag/Valores/",
};

/** Quantas linhas são renderizadas inicialmente e por chunk no lazy scroll */
const PAGE_SIZE = 100;

/** Distância em px até o fim do container que dispara o carregamento do próximo chunk */
const SCROLL_THRESHOLD = 200;

const BRL = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });
const formatBRL = (v) => BRL.format(Number(v) || 0);

const formatDataBR = (iso) => {
    if (!iso) return "—";
    const str = String(iso).slice(0, 10);
    const [y, m, d] = str.split("-");
    if (!y || !m || !d) return str;
    return `${d}/${m}/${y}`;
};

const Container = styled.div`
    width: 100%;
    height: 100dvh;
    background-color: #242d34;
    display: flex;
    flex-direction: column;
`;

const BodyWrapper = styled.div`
    width: 100%;
    flex: 1;
    padding: 15px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    gap: 12px;
`;

const HeaderCard = styled.div`
    width: 100%;
    background-color: #fff;
    border-radius: 15px;
    padding: 15px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const FiltersGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 10px;
    width: 100%;
`;

const ResumoWrapper = styled.div`
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
`;

const InfoCard = styled.div`
    flex: 1 1 220px;
    min-width: 180px;
    background-color: #f5f7fa;
    border: 1px solid rgba(0, 0, 0, 0.06);
    border-radius: 12px;
    padding: 12px 16px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 4px;
`;

const TotalCard = styled(InfoCard)`
    background: linear-gradient(135deg, #0f766e 0%, #14b8a6 100%);
    border: none;
    color: #fff;
`;

const InfoLabel = styled.span`
    font-size: 11px;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    opacity: 0.8;
    font-weight: 600;
`;

const InfoValue = styled.span`
    font-size: 22px;
    font-weight: 700;
    letter-spacing: -0.3px;
`;

const TableShell = styled.div`
    width: 100%;
    flex: 1;
    background-color: #ffffff;
    border-radius: 15px;
    overflow: hidden;
`;

const DeleteButton = styled.button`
    all: unset;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    color: #b91c1c;
    cursor: pointer;
    transition: background-color .15s ease, transform .15s ease;

    &:hover {
        background-color: rgba(185, 28, 28, 0.12);
        transform: scale(1.05);
    }
    &:focus-visible {
        outline: 2px solid #e11d48;
        outline-offset: 2px;
    }
`;

export default function FolhaPagamento() {
    const api = useApi();
    const { isModalOpen, openModal, closeModal } = useModal();

    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);

    const [matriculaFiltro, setMatriculaFiltro] = useState("");
    const [centrosCustoFiltro, setCentrosCustoFiltro] = useState([]);
    const [motivosFiltro, setMotivosFiltro] = useState([]);
    const [origensFiltro, setOrigensFiltro] = useState([]);
    const [dataInicio, setDataInicio] = useState("");
    const [dataFim, setDataFim] = useState("");

    const tableRef = useRef(null);
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

    const fetchValores = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(FOLHA_API.valores);
            setRows(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error(error);
            setRows([]);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        fetchValores();
    }, [fetchValores]);

    const handleOpenSidebar = () => {
        openModal("sidebarFolhaPag");
    }

    /* Lançamento selecionado para exclusão (apenas origem LANCAMENTOS
       permite soft delete via API/FolhaPag/DeleteLancamento). */
    const [lancamentoToDelete, setLancamentoToDelete] = useState(null);

    const handleRequestDelete = (row) => {
        setLancamentoToDelete(row);
        openModal("confirmDeleteLancamento");
    }

    const handleCloseDelete = () => {
        setLancamentoToDelete(null);
        closeModal();
    }

    /* Lazy loading: quando o usuário rola até perto do fim da tabela, carregamos
       o próximo chunk incrementando `visibleCount`. Quando um filtro muda, o chunk
       volta ao tamanho inicial e o scroll retorna ao topo para garantir que o
       primeiro conjunto visível seja coerente com o novo filtro. */

    useEffect(() => {
        const el = tableRef.current;
        if (!el) return;

        const handleScroll = () => {
            const { scrollTop, scrollHeight, clientHeight } = el;
            if (scrollTop + clientHeight >= scrollHeight - SCROLL_THRESHOLD) {
                setVisibleCount((prev) => prev + PAGE_SIZE);
            }
        };

        el.addEventListener("scroll", handleScroll, { passive: true });
        return () => el.removeEventListener("scroll", handleScroll);
    }, []);

    useEffect(() => {
        setVisibleCount(PAGE_SIZE);
        if (tableRef.current) {
            tableRef.current.scrollTop = 0;
        }
    }, [matriculaFiltro, centrosCustoFiltro, motivosFiltro, origensFiltro, dataInicio, dataFim]);

    const centrosCustoOpts = useMemo(
        () => [...new Set(rows.map((r) => r.centro_custo).filter(Boolean))].sort(),
        [rows]
    );

    const motivosOpts = useMemo(
        () => [...new Set(rows.map((r) => r.motivo).filter(Boolean))].sort(),
        [rows]
    );

    const origensOpts = useMemo(
        () => [...new Set(rows.map((r) => r.origem).filter(Boolean))].sort(),
        [rows]
    );

    const filtered = useMemo(() => {
        const mat = (matriculaFiltro || "").trim();
        const ccSet = new Set((centrosCustoFiltro || []).map((o) => o.value));
        const motSet = new Set((motivosFiltro || []).map((o) => o.value));
        const oriSet = new Set((origensFiltro || []).map((o) => o.value));

        return rows.filter((r) => {
            if (mat && !String(r.matricula || "").includes(mat)) return false;
            if (ccSet.size && !ccSet.has(r.centro_custo)) return false;
            if (motSet.size && !motSet.has(r.motivo)) return false;
            if (oriSet.size && !oriSet.has(r.origem)) return false;
            if (dataInicio && String(r.data) < dataInicio) return false;
            if (dataFim && String(r.data) > dataFim) return false;
            return true;
        });
    }, [rows, matriculaFiltro, centrosCustoFiltro, motivosFiltro, origensFiltro, dataInicio, dataFim]);

    const totalExibido = useMemo(
        () => filtered.reduce((acc, r) => acc + (Number(r.valor) || 0), 0),
        [filtered]
    );

    const columns = useMemo(
        () => [
            {
                id: "valor",
                header: "VALOR",
                accessor: (r) => r.valor,
                sortValue: (r) => Number(r.valor) || 0,
                render: (v) => formatBRL(v),
                align: "right",
                width: "130px",
            },
            {
                id: "motivo",
                header: "MOTIVO",
                accessor: (r) => r.motivo,
                width: "220px",
            },
            {
                id: "matricula",
                header: "MATRÍCULA",
                accessor: (r) => r.matricula,
                width: "110px",
            },
            {
                id: "colaborador",
                header: "COLABORADOR",
                accessor: (r) => r.colaborador,
                width: "240px",
            },
            {
                id: "data",
                header: "DATA",
                accessor: (r) => r.data,
                sortValue: (r) => r.data ?? "",
                render: (v) => formatDataBR(v),
                width: "110px",
            },
            {
                id: "centro_custo",
                header: "CENTRO DE CUSTO",
                accessor: (r) => r.centro_custo,
                width: "220px",
            },
            {
                id: "origem",
                header: "ORIGEM",
                accessor: (r) => r.origem,
                width: "170px",
            },
            {
                id: "observacoes",
                header: "OBSERVAÇÕES",
                accessor: (r) => r.observacoes,
                render: (v) => (v ? v : "—"),
            },
            {
                id: "acoes",
                header: "AÇÕES",
                accessor: () => null,
                sortable: false,
                align: "center",
                width: "80px",
                render: (_v, row) =>
                    row.origem === "LANCAMENTOS" ? (
                        <DeleteButton
                            type="button"
                            title="Excluir lançamento"
                            aria-label="Excluir lançamento"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleRequestDelete(row);
                            }}
                        >
                            <FiTrash2 size={18} />
                        </DeleteButton>
                    ) : null,
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    return (
        <Container>
            <NavBar title="FOLHA DE PAGAMENTO" modulo="rh" setSideBar={handleOpenSidebar} />

            <BodyWrapper>
                <HeaderCard>
                    <FiltersGrid>
                        <FilterContainer
                            type="text"
                            placeholder="MATRÍCULA"
                            maxLength={6}
                            valor={matriculaFiltro}
                            changeFunc={setMatriculaFiltro}
                        />
                        <FilterContainer
                            type="select"
                            placeholder="CENTRO DE CUSTO"
                            optType="obj"
                            options={centrosCustoOpts}
                            changeFunc={setCentrosCustoFiltro}
                        />
                        <FilterContainer
                            type="select"
                            placeholder="MOTIVO"
                            optType="obj"
                            options={motivosOpts}
                            changeFunc={setMotivosFiltro}
                        />
                        <FilterContainer
                            type="select"
                            placeholder="ORIGEM"
                            optType="obj"
                            options={origensOpts}
                            changeFunc={setOrigensFiltro}
                        />
                        <FilterContainer
                            type="date"
                            placeholder="DATA INÍCIO"
                            valor={dataInicio}
                            changeFunc={setDataInicio}
                        />
                        <FilterContainer
                            type="date"
                            placeholder="DATA FIM"
                            valor={dataFim}
                            changeFunc={setDataFim}
                        />
                    </FiltersGrid>

                    <ResumoWrapper>
                        <TotalCard>
                            <InfoLabel>TOTAL EXIBIDO</InfoLabel>
                            <InfoValue>{formatBRL(totalExibido)}</InfoValue>
                        </TotalCard>
                        <InfoCard>
                            <InfoLabel>LANÇAMENTOS</InfoLabel>
                            <InfoValue>{filtered.length}</InfoValue>
                        </InfoCard>
                    </ResumoWrapper>
                </HeaderCard>

                <TableShell>
                    <SortableTable
                        ref={tableRef}
                        columns={columns}
                        data={filtered}
                        limit={visibleCount}
                        isLoading={loading}
                        loadingText="Carregando folha de pagamento..."
                        emptyText="NENHUM LANÇAMENTO ENCONTRADO"
                        initialSort={{ key: "data", direction: "desc" }}
                        getRowId={(row, idx) =>
                            row.temp_id ?? `${row.origem}-${row.id}-${idx}`
                        }
                    />
                </TableShell>
            </BodyWrapper>

            {isModalOpen("sidebarFolhaPag") && (
                <SidebarFolhaPag dados={ filtered } />
            )}

            {isModalOpen("modalNovoLancamento") && (
                <ModalNovoLancamento
                    centrosCustoOpts={centrosCustoOpts}
                    onLancamentoCreated={fetchValores}
                />
            )}

            {isModalOpen("confirmDeleteLancamento") && (
                <ConfirmDeleteLancamento
                    lancamento={lancamentoToDelete}
                    fecharModal={handleCloseDelete}
                    onDelete={fetchValores}
                />
            )}
        </Container>
    );
}
